/// <reference types="vite-plugin-svgr/client" />
import { useTracking } from '@vp/react-tracking';
import React, { useRef, useState } from 'react';
import { Box, Card, FlexBox, Typography, responsive, tokensRaw, useScreenClass } from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { Appointment, DesignerReviewCriterionType } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import {
    DesignerReviewModal,
    ReviewRatings,
    useDesignerReviewGate,
    useSubmitDesignerReviewMutation,
} from '@99designs/reviews';
import { DesignLiveLogo } from '../assets/DesignLiveLogo';
import { CollaborationListItemFragment } from '../queries/ListingCollaborations.generated';
import { getAnalyticsData } from '../utils/getAnalyticsData';
import { getProjectDescription } from '../utils/getProjectDescription';
import { CTA, RequestCardCTA } from './RequestCardCTA';
import { RequestCardImage } from './RequestCardImage';
import { RequestCardSummary } from './RequestCardSummary';
import Toast from './Toast';

const formatAppointmentTime = (appointment: Appointment): string => {
    const timezone = appointment.timezone;
    const startTime = appointment.startTime;
    const durationMinutes = appointment.durationMinutes;

    const startDate = new Date(startTime);
    const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);

    return `${startDate.toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timezone,
        hour12: true,
    })} - ${endDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timezone,
        hour12: true,
    })}`;
};

const ResponsiveFlexBox = responsive(FlexBox);

const CollaborationRequestCard: React.FC<CollaborationListItemFragment> = ({
    url,
    thumbnailUrl,
    title,
    status,
    completion,
    createdAt,
    appointment,
    revisions,
    viewerRole,
    expertRequestId,
    isReviewEligible,
    lastDeliveringDesigner,
    __typename,
}) => {
    const { tracking } = useTracking();
    const trackFlyOut = (event: string, eventDetail?: string) =>
        tracking.track(event, {
            action: event,
            label: 'Designer Review Fly-Out',
            pageName: 'Design Services:Dashboard',
            pageSection: 'Design Services',
            pageStage: 'My Account',
            category: 'Fly-Out',
            eventDetail,
        });

    const [submitDesignerReview, { loading: isSubmitting }] = useSubmitDesignerReviewMutation();

    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [hasSubmittedReview, setHasSubmittedReview] = useState(false);
    const [toastStatus, setToastStatus] = useState<'success' | 'error' | undefined>(undefined);
    const reviewCompletedRef = useRef(false);

    const section = status !== 'COLLABORATION_STATUS_CANCELLED' ? 'current' : 'past';

    const message = getProjectDescription(status, completion?.reason);
    const statusIsInSession = status === 'COLLABORATION_STATUS_APPOINTMENT_IN_SESSION';
    const screenClass = useScreenClass();
    const isXs = screenClass === 'xs';

    const designerName = lastDeliveringDesigner?.freelancerId
        ? lastDeliveringDesigner.name
        : undefined;
    const designerAvatarUrl = lastDeliveringDesigner?.freelancerId
        ? `https://avatars.99static.com/avatars/${lastDeliveringDesigner.freelancerId}/128`
        : undefined;
    const isDesignerReviewEnabled = useDesignerReviewGate(isReviewEligible);

    const canReviewDesigner =
        isReviewEligible &&
        isDesignerReviewEnabled &&
        viewerRole !== 'VIEWER_ROLE_DESIGNER' &&
        !hasSubmittedReview;

    const ctas: CTA[] = [
        {
            label: __('View project'),
            iconType: 'workWithAPro',
            href: url,
            openInNewTab: false,
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...getAnalyticsData('/zoom', __('View project'), section),
                    action: 'Navigation Clicked',
                    navigationDetail: 'open-collaboration',
                });
            },
        },
    ];

    if (statusIsInSession && appointment) {
        ctas.push({
            label: __('Join session with Zoom'),
            iconType: 'phoneCall',
            href: appointment.zoomMeetingUrl,
            openInNewTab: false,
            skin: 'tertiary',
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...getAnalyticsData('/zoom', __('Join session with Zoom')),
                    action: 'Navigation Clicked',
                    navigationDetail: 'join-session',
                });
            },
        });
    }

    if (appointment?.canBeRescheduled) {
        ctas.push({
            label: __('Reschedule'),
            iconType: 'orderHistory',
            href: appointment.rescheduleUrl,
            openInNewTab: false,
            skin: 'tertiary',
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...getAnalyticsData(url, __('Reschedule')),
                    action: 'Navigation Clicked',
                    navigationDetail: 'reschedule-session',
                });
            },
        });
    }

    if (canReviewDesigner) {
        ctas.push({
            label: __('Review your designer'),
            iconType: 'star',
            skin: 'tertiary',
            onClick: () => {
                trackFlyOut('Fly-Out Viewed');
                setIsReviewModalOpen(true);
            },
        });
    }

    const handleReviewSubmit = async (ratings: ReviewRatings) => {
        try {
            await submitDesignerReview({
                variables: {
                    input: {
                        subjectId: expertRequestId,
                        subjectType: 'SUBJECT_COLLABORATION',
                        designerCanonicalId: lastDeliveringDesigner?.canonicalId ?? '',
                        overallRating: ratings.overall,
                        comment: ratings.comment,
                        criteria: ratings.criteria.map((c) => ({
                            type: c.type as DesignerReviewCriterionType,
                            rating: c.rating,
                        })),
                        submissionLocation: 'SUBMISSION_LOCATION_DASHBOARD',
                    },
                },
            });
            trackFlyOut('Fly-Out Clicked', 'Submit');
            setHasSubmittedReview(true);
            setToastStatus('success');
            reviewCompletedRef.current = true;
            setIsReviewModalOpen(false);
        } catch (error) {
            bugtracker.notify(error as Error, (event) => {
                event.addMetadata('submitDesignerReview', { expertRequestId });
            });
            setToastStatus('error');
            setIsReviewModalOpen(false);
        }
    };

    const handleReviewCancel = () => {
        trackFlyOut('Fly-Out Clicked', 'Cancel');
        reviewCompletedRef.current = true;
        setIsReviewModalOpen(false);
    };

    const handleReviewDismiss = () => {
        if (!reviewCompletedRef.current) {
            trackFlyOut('Fly-Out Closed');
        }
        reviewCompletedRef.current = false;
        setIsReviewModalOpen(false);
    };

    return (
        <>
            <Card bordered>
                <ResponsiveFlexBox
                    xs={{
                        flexDirection: 'column',
                        gap: 'between-subsections',
                        alignItems: 'center',
                    }}
                    sm={{
                        flexDirection: 'row',
                        gap: 'between-sections',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    <RequestCardImage imgSrc={thumbnailUrl} imgAlt={__('Design Preview')} />

                    {/* Collaboration details */}
                    <ResponsiveFlexBox
                        style={{ flex: '1 1 100%' }}
                        xs={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'between-subsections',
                        }}
                        sm={{ alignItems: 'flex-start' }}
                    >
                        <ResponsiveFlexBox
                            xs={{
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'between-actions',
                            }}
                            sm={{ alignItems: 'flex-start' }}
                        >
                            <RequestCardSummary
                                requestType={__typename}
                                title={title}
                                createdAt={createdAt}
                                linkUrl={url}
                                status={status}
                                completionReason={completion?.reason}
                                revisions={revisions}
                            />
                            {viewerRole !== 'VIEWER_ROLE_DESIGNER' && (
                                <Typography
                                    fontSkin="body-standard"
                                    textAlign={isXs ? 'center' : 'left'}
                                >
                                    {message}
                                </Typography>
                            )}
                        </ResponsiveFlexBox>
                        {appointment && (
                            <ResponsiveFlexBox
                                xs={{ flexDirection: 'column', alignItems: 'center', gap: '3' }}
                                sm={{ alignItems: 'flex-start' }}
                            >
                                <DesignLiveLogo />
                                <Typography
                                    fontSkin="body-small-bold"
                                    textColor="subtle"
                                    style={{ textAlign: isXs ? 'center' : 'left' }}
                                >
                                    {formatAppointmentTime(appointment)}
                                </Typography>
                            </ResponsiveFlexBox>
                        )}
                    </ResponsiveFlexBox>

                    {/* Collaboration CTA */}
                    <RequestCardCTA ctas={ctas} />
                </ResponsiveFlexBox>
            </Card>
            {isReviewModalOpen && (
                <DesignerReviewModal
                    isOpen={isReviewModalOpen}
                    designerName={designerName}
                    designerAvatarUrl={designerAvatarUrl}
                    onSubmit={handleReviewSubmit}
                    onSkip={handleReviewCancel}
                    onRequestDismiss={handleReviewDismiss}
                    isSubmitting={isSubmitting}
                />
            )}
            {toastStatus && (
                <Box
                    style={{
                        bottom: tokensRaw.SwanSemSpace5,
                        position: 'fixed',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        maxWidth: '653px',
                        width: `calc(100% - ${tokensRaw.SwanSemSpace5} * 2)`,
                        zIndex: tokensRaw.SwanSemZIndexRaised + 1,
                    }}
                >
                    <Toast
                        message={
                            toastStatus === 'success'
                                ? __('Thank you, your review has been submitted.')
                                : __("Sorry, we couldn't submit your review. Please try again.")
                        }
                        skin={toastStatus}
                        onDismiss={() => setToastStatus(undefined)}
                    />
                </Box>
            )}
        </>
    );
};

export { CollaborationRequestCard };
